(() => {
  const PARAM_LIMITS = {
    product: 200,
    sku: 100,
    variant: 200,
    quantity: 8,
    support: 40,
    product_url: 1000,
    variant_id: 32,
  };

  const ALLOWED_SUPPORT_TYPES = new Set(['プラスチックタイプ', '亜鉛メッキ支柱']);

  const readParam = (params, name) => {
    const value = String(params.get(name) || '').trim();
    return value.slice(0, PARAM_LIMITS[name] || 200);
  };

  const normalizedProductUrl = (value, variantId) => {
    if (!value || !variantId) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || url.protocol !== 'https:') return null;
      url.pathname = url.pathname.replace(/\/$/, '');
      if (!/^\/products\/[^/]+$/.test(url.pathname)) return null;
      url.search = '';
      url.hash = '';
      url.searchParams.set('variant', variantId);
      return url;
    } catch (_error) {
      return null;
    }
  };

  const normalizedQuantity = (value) => {
    const quantity = Number.parseInt(value, 10);
    if (!Number.isFinite(quantity)) return '1';
    return String(Math.min(9999, Math.max(1, quantity)));
  };

  // 商品ページで入れた項目（ご用途・型番など）。必須なのに空のものは「未入力」と出す
  const propertyValue = (property) => property.value || '未入力';
  const propertyLines = (properties) => (properties || []).map((property) => `${property.name}：${propertyValue(property)}`);

  const verifiedProductContext = async (params, properties = []) => {
    const variantId = readParam(params, 'variant_id').replace(/\D/g, '');
    const productUrl = normalizedProductUrl(readParam(params, 'product_url'), variantId);
    if (!productUrl) return null;

    try {
      const response = await fetch(`${productUrl.pathname}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(8000),
      });
      if (!response.ok) return null;

      const product = await response.json();
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const variant = variants.find((item) => String(item.id) === variantId);
      if (!variant || !product?.title) return null;
      const requestedSupport = readParam(params, 'support');
      const support = productUrl.pathname === '/products/sheet-pond-round-tank'
        && ALLOWED_SUPPORT_TYPES.has(requestedSupport)
        ? requestedSupport
        : '';

      return {
        product: String(product.title).slice(0, PARAM_LIMITS.product),
        sku: String(variant.sku || '').slice(0, PARAM_LIMITS.sku),
        variant: variant.title === 'Default Title' ? '' : String(variant.title || '').slice(0, PARAM_LIMITS.variant),
        quantity: normalizedQuantity(readParam(params, 'quantity')),
        support,
        product_url: productUrl.href,
        variant_id: variantId,
        properties: Array.isArray(properties) ? properties : [],
      };
    } catch (_error) {
      return null;
    }
  };

  const setOptionalText = (root, selector, labelSelector, value) => {
    const target = root.querySelector(selector);
    const label = root.querySelector(labelSelector);
    if (target) target.textContent = value;
    if (target) target.hidden = !value;
    if (label) label.hidden = !value;
  };

  const setupDraftPersistence = (form, variantId) => {
    const fields = [
      '#ContactForm-company',
      '#ContactForm-contact-method',
      '#ContactForm-contact-time',
      '#ContactForm-topic',
      '#ContactForm-postal-code',
      '#ContactForm-address-area',
      '#ContactForm-address-street',
      '#ContactForm-address-building',
      '#ContactForm-destination',
      '#ContactForm-delivery-date',
      '#ContactForm-installation',
    ];
    const storageKey = `jp-ecol-contact-draft:${variantId || 'general'}`;
    const postedSuccessfully = form.querySelector('[data-jp-ecol-contact-success]')?.dataset.jpEcolContactSuccess === 'true';

    try {
      if (postedSuccessfully) {
        sessionStorage.removeItem(storageKey);
        return;
      }
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
      fields.forEach((selector) => {
        const field = form.querySelector(selector);
        if (!field || typeof saved[selector] !== 'string') return;
        // 選択欄は初めから値がある（例：連絡方法の「メールを希望」）ので、保存した値が選択肢にあれば戻す
        if (field.tagName === 'SELECT') {
          if (saved[selector] && Array.from(field.options).some((option) => option.value === saved[selector])) field.value = saved[selector];
          return;
        }
        if (!field.value) field.value = saved[selector];
      });
      form.addEventListener('submit', () => {
        const draft = {};
        fields.forEach((selector) => {
          const field = form.querySelector(selector);
          if (field) draft[selector] = field.value;
        });
        sessionStorage.setItem(storageKey, JSON.stringify(draft));
      });
    } catch (_error) {
      // The form still works when browser storage is unavailable.
    }
  };

  const setupPhoneRequirement = (form) => {
    const contactMethod = form.querySelector('#ContactForm-contact-method');
    const phone = form.querySelector('#ContactForm-phone');
    const badge = form.querySelector('[data-jp-ecol-phone-requirement]');
    if (!contactMethod || !phone || !badge) return;

    const syncRequirement = () => {
      const phoneRequired = ['電話を希望', 'どちらでも可'].includes(contactMethod.value);
      phone.required = phoneRequired;
      if (phoneRequired) {
        phone.setAttribute('aria-required', 'true');
      } else {
        phone.removeAttribute('aria-required');
      }
      badge.textContent = phoneRequired ? '必須' : '任意';
      badge.classList.toggle('jp-ecol-contact-required', phoneRequired);
      badge.classList.toggle('jp-ecol-contact-optional', !phoneRequired);
    };

    contactMethod.addEventListener('change', syncRequirement);
    syncRequirement();
  };

  const normalizedPostalCode = (value) => String(value || '').normalize('NFKC').replace(/\D/g, '').slice(0, 7);

  const formattedPostalCode = (value) => {
    const digits = normalizedPostalCode(value);
    return digits.length === 7 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : value;
  };

  const setupPostalLookup = (form) => {
    const postalCode = form.querySelector('#ContactForm-postal-code');
    const addressArea = form.querySelector('#ContactForm-address-area');
    const status = form.querySelector('[data-jp-ecol-postal-status]');
    if (!postalCode || !addressArea || !status) return async () => {};

    let debounceTimer;
    let requestController;
    let requestSequence = 0;
    let lastResolvedCode = addressArea.value ? normalizedPostalCode(postalCode.value) : '';

    const setStatus = (message = '', state = '') => {
      status.textContent = message;
      status.dataset.state = state;
      status.hidden = !message;
    };

    // 自動で入れた部分だけを扱う。お客様が足した番地・建物名は消さない
    let lastAutoArea = addressArea.value || '';
    const clearAddress = () => {
      if (!addressArea.value || addressArea.value !== lastAutoArea) return;
      addressArea.value = '';
      lastAutoArea = '';
      addressArea.dispatchEvent(new Event('input', { bubbles: true }));
    };
    const applyArea = (area) => {
      const current = addressArea.value;
      const isAutoBased = !current || (lastAutoArea && current.startsWith(lastAutoArea));
      if (!isAutoBased && !current.startsWith(area)) {
        // お客様が自分で書いた住所は上書きしない
        return false;
      }
      const rest = lastAutoArea && current.startsWith(lastAutoArea) ? current.slice(lastAutoArea.length) : '';
      addressArea.value = current.startsWith(area) && !rest ? current : area + rest;
      lastAutoArea = area;
      addressArea.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    };

    const lookupPostalCode = async () => {
      const digits = normalizedPostalCode(postalCode.value);
      if (digits.length !== 7) {
        lastResolvedCode = '';
        clearAddress();
        setStatus();
        return;
      }

      postalCode.value = formattedPostalCode(digits);
      if (digits === lastResolvedCode && addressArea.value) return;

      requestController?.abort();
      const controller = new AbortController();
      requestController = controller;
      const requestId = ++requestSequence;
      const timeoutId = window.setTimeout(() => controller.abort(), 5000);
      setStatus('住所を検索しています。', 'loading');

      try {
        const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${digits}&limit=1`, {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Postal lookup failed');

        const data = await response.json();
        if (requestId !== requestSequence) return;
        const result = Array.isArray(data?.results) ? data.results[0] : null;
        const area = `${result?.address1 || ''}${result?.address2 || ''}${result?.address3 || ''}`.trim();
        if (!area) {
          lastResolvedCode = '';
          clearAddress();
          setStatus('該当する住所が見つかりませんでした。郵便番号をご確認ください。', 'error');
          return;
        }

        const applied = applyArea(area);
        lastResolvedCode = digits;
        setStatus(applied ? '住所を入れました。続けて番地を入力してください。' : `郵便番号の住所：${area}（入力済みの住所はそのままにしています）`, 'success');
      } catch (_error) {
        if (requestId !== requestSequence) return;
        lastResolvedCode = '';
        clearAddress();
        setStatus('住所を取得できませんでした。郵便番号をご確認ください。', 'error');
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    const scheduleLookup = () => {
      window.clearTimeout(debounceTimer);
      const digits = normalizedPostalCode(postalCode.value);
      if (digits.length !== 7) {
        requestSequence += 1;
        requestController?.abort();
        lastResolvedCode = '';
        clearAddress();
        setStatus();
        return;
      }
      debounceTimer = window.setTimeout(() => void lookupPostalCode(), 250);
    };

    postalCode.addEventListener('input', scheduleLookup);
    postalCode.addEventListener('blur', () => {
      window.clearTimeout(debounceTimer);
      void lookupPostalCode();
    });
    if (normalizedPostalCode(postalCode.value).length === 7) void lookupPostalCode();

    return lookupPostalCode;
  };

  // 郵便番号→住所→番地→建物名は、キーボードの「次へ」（Enter）で順に進める。自動で入った住所は飛ばして番地へ
  const setupAddressKeyboard = (form) => {
    const postalCode = form.querySelector('#ContactForm-postal-code');
    const addressArea = form.querySelector('#ContactForm-address-area');
    const street = form.querySelector('#ContactForm-address-street');
    const building = form.querySelector('#ContactForm-address-building');
    const destination = form.querySelector('#ContactForm-destination');
    if (!postalCode || !addressArea || !street || !building) return;

    const nextField = new Map([
      [postalCode, () => (addressArea.value ? street : addressArea)],
      [addressArea, () => street],
      [street, () => building],
      [building, () => destination],
    ]);
    nextField.forEach((getNext, field) => {
      field.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;
        const next = getNext();
        if (!next) return;
        event.preventDefault();
        next.focus();
      });
    });
  };

  const setupReviewStep = (form, beforeReview) => {
    const inputStep = form.querySelector('[data-jp-ecol-contact-input-step]');
    const reviewStep = form.querySelector('[data-jp-ecol-contact-review-step]');
    const reviewList = form.querySelector('[data-jp-ecol-contact-review-list]');
    const openButton = form.querySelector('[data-jp-ecol-contact-review-open]');
    const editButton = form.querySelector('[data-jp-ecol-contact-review-edit]');
    const submitButton = form.querySelector('[data-jp-ecol-contact-review-submit]');
    if (!inputStep || !reviewStep || !reviewList || !openButton || !editButton || !submitButton) return;

    let reviewConfirmed = false;

    const reviewValue = (field) => {
      const value = String(field.dataset.jpEcolReviewValue ?? field.value ?? '').trim();
      if (!value || field.dataset.jpEcolReviewFormat !== 'date') return value;
      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
    };

    const populateReview = () => {
      reviewList.replaceChildren();
      form.querySelectorAll('[data-jp-ecol-review-label]').forEach((field) => {
        if (field.disabled) return;
        const value = reviewValue(field);
        if (!value) return;
        const term = document.createElement('dt');
        const detail = document.createElement('dd');
        term.textContent = field.dataset.jpEcolReviewLabel;
        detail.textContent = value;
        reviewList.append(term, detail);
      });
    };

    const showReview = async () => {
      if (typeof beforeReview === 'function' && await beforeReview() === false) return;
      if (!form.reportValidity()) return;
      reviewConfirmed = false;
      populateReview();
      inputStep.hidden = true;
      reviewStep.hidden = false;
      reviewStep.focus({ preventScroll: true });
      reviewStep.scrollIntoView({ behavior: 'auto', block: 'start' });
    };

    const showInput = () => {
      reviewConfirmed = false;
      reviewStep.hidden = true;
      inputStep.hidden = false;
      openButton.focus({ preventScroll: true });
      form.scrollIntoView({ behavior: 'auto', block: 'start' });
    };

    openButton.addEventListener('click', () => void showReview());
    editButton.addEventListener('click', showInput);
    submitButton.addEventListener('click', () => {
      reviewConfirmed = true;
    });
    form.addEventListener('submit', (event) => {
      if (reviewConfirmed) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void showReview();
    });
  };

  const initContactForm = async (scope = document) => {
    const form = scope.querySelector?.('#ContactForm') || document.querySelector('#ContactForm');
    if (!form || form.dataset.jpEcolContactInitialized === 'true') return;
    form.dataset.jpEcolContactInitialized = 'true';

    const params = new URLSearchParams(window.location.search);
    const variantId = readParam(params, 'variant_id').replace(/\D/g, '');
    setupDraftPersistence(form, variantId);
    setupPhoneRequirement(form);
    setupAddressKeyboard(form);
    const ensurePostalArea = setupPostalLookup(form);
    const topic = form.querySelector('#ContactForm-topic');
    const body = form.querySelector('#ContactForm-body');
    const bodyBadge = form.querySelector('[data-jp-ecol-body-requirement]');
    const quotePanel = form.querySelector('[data-jp-ecol-quote-list]');
    const quoteItems = form.querySelector('[data-jp-ecol-quote-items]');
    const quoteError = form.querySelector('[data-jp-ecol-quote-error]');
    const quoteSummary = form.querySelector('[data-jp-ecol-quote-summary]');
    const quoteStore = window.JpEcolQuoteList;
    const isList = params.get('quote') === 'list';
    let contexts = [];
    let loading = true;
    let invalidList = false;
    let records = [];
    // 空のまま確認へ進んだ時に、何を書けばよいか分かる言葉で知らせる
    const addressMessages = [
      [form.querySelector('#ContactForm-address-area'), '住所（都道府県・市区町村・町名）を入力してください。郵便番号を入れると自動で入ります。'],
      [form.querySelector('#ContactForm-address-street'), '番地を入力してください（例：1-2-3）。番地がない場合は「なし」とご記入ください。'],
    ].filter(([field]) => field);
    const syncAddressMessages = () => {
      addressMessages.forEach(([field, message]) => {
        field.setCustomValidity(field.required && !field.value.trim() ? message : '');
      });
    };
    addressMessages.forEach(([field]) => field.addEventListener('input', syncAddressMessages));
    const syncRequirements = () => {
      const optional = contexts.length > 0 && topic.value === '見積もり・購入';
      body.required = !optional;
      body.setAttribute('aria-required', String(!optional));
      bodyBadge.textContent = optional ? '任意' : '必須';
      bodyBadge.className = optional ? 'jp-ecol-contact-optional' : 'jp-ecol-contact-required';
      body.placeholder = optional
        ? '用途、迷っていること、確認したいことなど。見積もりのみの場合は空欄でかまいません。'
        : '用途、迷っていること、確認したいことなどをご記入ください。';
      form.querySelector('.jp-ecol-contact-examples').hidden = optional;
      // 見積もり・送料の問い合わせは、送料を出すために郵便番号・住所・番地を必須にする（10/5 日本エコル様の依頼、10/6 番地を分けて必須に）
      const addressRequired = contexts.length > 0 || ['見積もり・購入', '在庫・納期', '送料・配送', '設置・搬入'].includes(topic.value);
      [
        ['#ContactForm-postal-code', '[data-jp-ecol-postal-requirement]'],
        ['#ContactForm-address-area', '[data-jp-ecol-address-requirement]'],
        ['#ContactForm-address-street', '[data-jp-ecol-street-requirement]'],
      ].forEach(([fieldSelector, badgeSelector]) => {
        const field = form.querySelector(fieldSelector);
        const badge = form.querySelector(badgeSelector);
        if (!field || !badge) return;
        field.required = addressRequired;
        field.setAttribute('aria-required', String(addressRequired));
        badge.textContent = addressRequired ? '必須' : '任意';
        badge.className = addressRequired ? 'jp-ecol-contact-required' : 'jp-ecol-contact-optional';
      });
      syncAddressMessages();
    };
    const summaryText = ({ forReview = false } = {}) => contexts.map((item, index) => [
      `${index + 1}. ${item.product}`,
      item.variant, item.support ? `支柱：${item.support}` : '',
      `数量：${item.quantity}`,
      ...propertyLines(item.properties),
      ...(forReview ? [] : [item.sku ? `商品コード：${item.sku}` : '', item.product_url]),
    ].filter(Boolean).join('\n')).join('\n\n');
    const syncSummary = () => {
      quoteSummary.value = summaryText();
      quoteSummary.dataset.jpEcolReviewValue = summaryText({ forReview: true });
      quoteSummary.disabled = !isList || contexts.length === 0;
      syncRequirements();
    };
    const setQuoteError = (message) => {
      quoteError.textContent = message;
      quoteError.hidden = !message;
    };
    const renderList = () => {
      quoteItems.replaceChildren();
      contexts.forEach((item, index) => {
        const row = document.createElement('div');
        row.className = 'jp-ecol-quote-item';
        const description = document.createElement('div');
        const link = document.createElement('a');
        const title = document.createElement('strong');
        title.textContent = item.product;
        link.href = item.product_url;
        link.append(title);
        const detail = document.createElement('small');
        detail.textContent = [item.variant, item.support, item.sku].filter(Boolean).join(' / ');
        description.append(link, detail);
        propertyLines(item.properties).forEach((line) => {
          const property = document.createElement('small');
          property.textContent = line;
          description.append(property);
        });
        const label = document.createElement('label');
        label.textContent = '数量';
        const quantity = document.createElement('input');
        quantity.type = 'number';
        quantity.min = '1';
        quantity.max = '9999';
        quantity.step = '1';
        quantity.required = true;
        quantity.value = item.quantity;
        quantity.setAttribute('aria-label', `${item.product}の数量`);
        quantity.addEventListener('change', () => {
          if (!quantity.reportValidity()) return;
          const previous = records[index].quantity;
          records[index].quantity = Number(quantity.value);
          try {
            quoteStore.write(records);
            item.quantity = quantity.value;
            setQuoteError('');
            syncSummary();
          } catch (_error) {
            records[index].quantity = previous;
            quantity.value = String(previous);
            setQuoteError('数量を保存できませんでした。もう一度お試しください。');
          }
        });
        label.append(quantity);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.append(form.querySelector('[data-jp-ecol-quote-remove-icon]').content.cloneNode(true));
        remove.title = 'お問い合わせリストから削除';
        remove.setAttribute('aria-label', `${item.product}をお問い合わせリストから削除`);
        remove.addEventListener('click', () => {
          const next = records.filter((_item, i) => i !== index);
          try {
            quoteStore.write(next);
            records = next;
            contexts.splice(index, 1);
            renderList();
          } catch (_error) { setQuoteError('削除できませんでした。もう一度お試しください。'); }
        });
        row.append(description, label, remove);
        quoteItems.append(row);
      });
      setQuoteError(contexts.length ? '' : 'お問い合わせリストに商品がありません。');
      syncSummary();
    };
    topic.addEventListener('change', syncRequirements);
    setupReviewStep(form, async () => {
      if (loading) return false;
      if (isList && (invalidList || !contexts.length)) {
        quotePanel.scrollIntoView({ block: 'start' });
        return false;
      }
      if (isList) {
        const quantities = Array.from(quoteItems.querySelectorAll('input[type="number"]'));
        if (quantities.some((input) => !input.reportValidity())) return false;
        const next = records.map((record, index) => ({ ...record, quantity: Number(quantities[index].value) }));
        try {
          quoteStore.write(next);
          records = next;
          contexts.forEach((item, index) => { item.quantity = String(next[index].quantity); });
          setQuoteError('');
        } catch (_error) {
          setQuoteError('数量を保存できませんでした。もう一度お試しください。');
          return false;
        }
      }
      await ensurePostalArea();
      syncSummary();
      form.querySelector('[data-jp-ecol-contact-body]').value = [topic.value, summaryText(), body.value.trim()].filter(Boolean).join('\n\n');
      return true;
    });
    if (form.querySelector('[data-jp-ecol-contact-success]')?.dataset.jpEcolContactSuccess === 'true') {
      if (isList) { try { quoteStore.write([]); } catch (_error) { /* Submitted inquiry is already saved by Shopify. */ } }
      quoteStore?.clearHandoff?.();
      return;
    }
    if (isList) {
      quotePanel.hidden = false;
      setQuoteError('商品情報を確認しています。');
      topic.value = '見積もり・購入';
      try {
        records = quoteStore.read();
        // Verify every line against Shopify before using it in the inquiry.
        for (const record of records) {
          const item = await verifiedProductContext(new URLSearchParams({ product_url: record.product_url, variant_id: record.variant_id, quantity: record.quantity, support: record.support }), record.properties);
          if (!item) throw new Error('unverified-product');
          contexts.push(item);
        }
        renderList();
      } catch (_error) {
        contexts = [];
        invalidList = true;
        setQuoteError('商品情報を確認できませんでした。再読み込みするか、商品ページから選び直してください。');
      }
      loading = false;
      return;
    }
    const context = await verifiedProductContext(params, quoteStore?.readHandoff?.(variantId) || []);
    loading = false;
    if (params.get('intent') === 'advice') topic.value = '商品選び・仕様の相談';
    if (!context) {
      if (params.has('variant_id')) {
        quotePanel.hidden = false;
        setQuoteError('商品情報を確認できませんでした。商品名をご相談内容に記入するか、商品ページから選び直してください。');
      }
      syncRequirements();
      return;
    }
    contexts = [context];
    if (params.get('intent') !== 'advice') topic.value = '見積もり・購入';
    syncRequirements();

    form.querySelectorAll('[data-jp-ecol-contact-param]').forEach((input) => {
      const value = context[input.dataset.jpEcolContactParam] || '';
      input.value = value;
      input.disabled = !value;
    });

    const summary = form.querySelector('[data-jp-ecol-contact-product]');
    const productName = form.querySelector('[data-jp-ecol-contact-product-name]');
    const productLink = form.querySelector('[data-jp-ecol-contact-product-link]');
    if (productName) productName.textContent = context.product;
    if (productLink) productLink.href = context.product_url;
    setOptionalText(form, '[data-jp-ecol-contact-sku]', '[data-jp-ecol-contact-sku-label]', context.sku);
    setOptionalText(form, '[data-jp-ecol-contact-variant]', '[data-jp-ecol-contact-variant-label]', context.variant);
    setOptionalText(form, '[data-jp-ecol-contact-support]', '[data-jp-ecol-contact-support-label]', context.support);
    setOptionalText(form, '[data-jp-ecol-contact-quantity]', '[data-jp-ecol-contact-quantity-label]', context.quantity);
    // 入力した項目を「お問い合わせ商品」の枠・確認画面・送信メールに出す
    const propertyList = summary?.querySelector('dl');
    const propertyInput = form.querySelector('[data-jp-ecol-contact-properties]');
    context.properties.forEach((property) => {
      const term = document.createElement('dt');
      const detail = document.createElement('dd');
      term.textContent = property.name;
      detail.textContent = propertyValue(property);
      propertyList?.append(term, detail);
      // 確認画面に1項目ずつ出すための欄（name がないので送信はされない）
      const reviewField = document.createElement('input');
      reviewField.type = 'hidden';
      reviewField.value = propertyValue(property);
      reviewField.dataset.jpEcolReviewLabel = property.name;
      propertyInput?.before(reviewField);
    });
    if (propertyInput) {
      propertyInput.value = propertyLines(context.properties).join('\n');
      propertyInput.disabled = !context.properties.length;
    }
    if (summary) summary.hidden = false;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void initContactForm());
  } else {
    void initContactForm();
  }

  document.addEventListener('shopify:section:load', (event) => void initContactForm(event.target));
})();
